import React, { useState, useEffect } from "react";
import { useCountryContext } from "../../../context/CountryContext";
import {
  Select,
  TextArea,
  MyMessage,
  InputField,
  SelectInput,
  MyButton,
  AddPageShell,
  AddSection,
} from "../../components/Component";
import { getAddLevel } from "../../../lib/sysNavigation/SysNavigation";
import { requireFields } from "../../../lib/validators";
import { defaultImage } from "../../../lib/libraries/imageKit/ImageKit";
import { ImageKitUpload } from "../../../ui/ImageKitLibrary";
import { FOLDERS, image_kit_folder } from "../../doseeData/utils/Utils";
import { useBaseLevelContext } from "../../../context/levels/BaseLevelContext";
import GenerateDescription from "../../doseeData/utils/GenerateDescription";
import { FaHandSparkles } from "react-icons/fa";
import {
  FiLayers,
  FiImage,
  FiAlignLeft,
  FiShield,
  FiInfo,
} from "react-icons/fi";

const levelsRoute = "/levels";

const Add = () => {
  const { getLevelsByCountryId, create } = useBaseLevelContext();
  const { countries, fetchCountries } = useCountryContext();

  const [firstLevels, setFirstLevels] = useState<any>(null);
  const [secondLevels, setSecondLevels] = useState<any>(null);
  const [thirdLevels, setThirdLevels] = useState<any>(null);

  const [isCity, setIsCity] = useState(0);
  const [countryId, setCountryId] = useState<any>(null);
  const [countryName, setCountryName] = useState<any>(null);
  const [thirdLevelId, setThirdLevelId] = useState<any>(null);
  const [firstLevelId, setFirstLevelId] = useState<any>(null);
  const [secondLevelId, setSecondLevelId] = useState<any>(null);

  const [thumbnail, setThumbnail] = useState(defaultImage);
  const [thumbnailId, setThumbnailId] = useState("img_cdn_id");

  const [gdp, set_gdp] = useState(null);
  const [levelName, set_levelName] = useState(null);
  const [population, set_population] = useState(null);
  const [description, set_description] = useState(null);

  const [tourism, set_tourism] = useState("safe");
  const [safety_indicator, set_safety_indicator] = useState("safe");

  const [error, setError] = useState(false);
  const [message, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showDescriptionModal, setShowDescriptionModal] = useState<any>(null);

  const label = getAddLevel() ? getAddLevel() : "First";

  const cleanFields = () => {
    setLoading(false);
    setSuccess(true);
    setThumbnail(null);
    setThumbnailId(null);
    set_gdp(null);
    set_levelName(null);
    set_population(null);
    set_description(null);
  };

  useEffect(() => {
    if (!countries) fetchCountries();
  }, [countries, fetchCountries]);

  const responses = (isError: any, msg: any) => {
    setError(isError);
    setSuccess(!isError);
    setErrorMessage(msg);
  };

  const sendAddCountryRequest = async (e: any) => {
    e.preventDefault();
    setLoading(true);
    const validation = requireFields({
      GDP: gdp,
      Name: levelName,
      Population: population,
      Tourism: tourism,
      "Safety Indicator": safety_indicator,
      Description: description,
    });
    if (validation.isError) {
      setLoading(false);
      return responses(validation.isError, validation.message);
    } else {
      if (!thumbnail || !thumbnailId) {
        setLoading(false);
        return responses(true, "Image Required");
      } else {
        await sendDBRequest();
      }
    }
  };

  const fetchAllLevelsByCountryId = async (cid: any) => {
    const first = await getLevelsByCountryId({
      level: "first",
      country_id: cid,
      page: 1,
      pageSize: 250,
    });
    const second = await getLevelsByCountryId({
      level: "second",
      country_id: cid,
      page: 1,
      pageSize: 250,
    });
    const third = await getLevelsByCountryId({
      level: "third",
      country_id: cid,
      page: 1,
      pageSize: 250,
    });
    if (first?.data) setFirstLevels(first?.data);
    if (second?.data) setSecondLevels(second?.data);
    if (third?.data) setThirdLevels(third?.data);
  };

  const sendDBRequest = async () => {
    const dataObject: Record<string, any> = {
      country_id: countryId,
      name: levelName,
      slug: (levelName as any).toLowerCase().replace(/\s/g, "_"),
      thumbnail: thumbnail ? thumbnail : defaultImage,
      image_cdn_id: thumbnailId ? thumbnailId : "x",
      description: description,
      population: population,
      is_city: isCity,
      main_city: levelName,
      gdp: gdp,
      tourism: tourism,
      safety_indicator: safety_indicator,
      status: 1,
    };

    if (label === "First") {
      dataObject.first_level_label = levelName;
    } else if (label === "Second") {
      dataObject.first_level_id = firstLevelId;
      dataObject.second_level_label = levelName;
    } else if (label === "Third") {
      dataObject.first_level_id = firstLevelId;
      dataObject.second_level_id = secondLevelId;
      dataObject.third_level_label = levelName;
    } else if (label === "Fourth") {
      dataObject.first_level_id = firstLevelId;
      dataObject.second_level_id = secondLevelId;
      dataObject.third_level_id = thirdLevelId;
      dataObject.fourth_level_label = levelName;
    }

    setLoading(true);
    const { message: msg, status } = await create({
      data: dataObject,
      level: String(label).toLowerCase(),
    });
    setLoading(false);
    if (status === 200 || status === 201) {
      cleanFields();
      return responses(false, msg);
    } else {
      return responses(true, msg);
    }
  };

  return (
    <AddPageShell
      backRoute={`${levelsRoute}/${label.toString().toLowerCase()}`}
      eyebrow={`Geography • ${label} Level`}
      title={`Add ${label} Level`}
      subtitle="Place this level inside its country and provide identity, media and safety info."
      icon={<FiLayers className="h-5 w-5" />}
      pills={[
        { label: "Level", value: label },
        { label: "Country", value: countryName || "—" },
      ]}
      modals={
        showDescriptionModal ? (
          <GenerateDescription
            setDescription={set_description}
            setShowDescriptionModal={setShowDescriptionModal}
          />
        ) : null
      }
      loading={loading}
      submitLabel={`Add ${label} Level`}
      onSubmit={sendAddCountryRequest}
      onSubmitClick={() => {
        setError(false);
        setSuccess(false);
        setErrorMessage(null);
      }}
      message={
        <MyMessage
          error={error}
          message={message}
          success={success}
          onExpired={() => {
            setError(false);
            setSuccess(false);
          }}
          onClick={() => {
            setError(false);
            setSuccess(false);
            setErrorMessage("");
          }}
        />
      }
    >
      <AddSection
        title="Hierarchy"
        hint="Pick the country and parent levels this entry sits inside."
        icon={<FiLayers className="h-4 w-4" />}
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3 xl:grid-cols-4">
          <SelectInput
            options={
              countries
                ? ["Select a Country", ...countries?.map((c: any) => c?.name)]
                : []
            }
            title={"Countries"}
            onChange={(e: any) => {
              const country = countries?.find(
                (ct: any) => ct?.name === e.target.value,
              );
              setCountryId(country?.country_id);
              setCountryName(country?.name);
              setFirstLevels(null);
              setSecondLevels(null);
              setThirdLevels(null);
              if (country?.country_id)
                fetchAllLevelsByCountryId(country?.country_id);
            }}
          />
          {["Second", "Third", "Fourth"].includes(label) &&
          countryId &&
          firstLevels ? (
            <SelectInput
              options={
                firstLevels
                  ? [
                      "Select a first level",
                      ...firstLevels?.map((f: any) => f?.name),
                    ]
                  : []
              }
              title={"First Levels"}
              onChange={(e: any) => {
                const id = firstLevels?.find(
                  (f: any) => f?.name === e.target.value,
                )?.first_level_id;
                setFirstLevelId(id);
              }}
            />
          ) : null}
          {["Third", "Fourth"].includes(label) && countryId && secondLevels ? (
            <SelectInput
              options={
                secondLevels
                  ? [
                      "Select a second level",
                      ...secondLevels?.map((s: any) => s?.name),
                    ]
                  : []
              }
              title={"Second Levels"}
              onChange={(e: any) => {
                const id = secondLevels?.find(
                  (s: any) => s?.name === e.target.value,
                )?.second_level_id;
                setSecondLevelId(id);
              }}
            />
          ) : null}
          {["Fourth"].includes(label) && countryId && thirdLevels ? (
            <SelectInput
              options={
                thirdLevels
                  ? [
                      "Select a third level",
                      ...thirdLevels?.map((t: any) => t?.name),
                    ]
                  : []
              }
              title={"Third Levels"}
              onChange={(e: any) => {
                const id = thirdLevels?.find(
                  (t: any) => t?.name === e.target.value,
                )?.third_level_id;
                setThirdLevelId(id);
              }}
            />
          ) : null}
        </div>
      </AddSection>

      <AddSection
        title="Media"
        hint="Upload a representative image for this level."
        icon={<FiImage className="h-4 w-4" />}
      >
        {countries && countryId ? (
          <ImageKitUpload
            thumbnail={thumbnail}
            thumbnailId={thumbnailId}
            setThumbnail={setThumbnail}
            setThumbnailId={setThumbnailId}
            folder={image_kit_folder(countryName, FOLDERS.LEVELS)}
          />
        ) : (
          <div className="flex items-center gap-2 rounded-xl border border-dashed border-gray-300 bg-gray-50 p-6 text-sm text-gray-700">
            <FiInfo className="h-4 w-4" />
            Select a country to enable image upload.
          </div>
        )}
      </AddSection>

      <AddSection
        title="Level Details"
        hint="Identity, classification and key indicators."
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3 xl:grid-cols-4">
          <InputField
            props={{
              id: "levelName",
              title: `${label} Level Name`,
              type: "text",
              required: true,
              value: levelName ? levelName : "",
              onChange: (e: any) => {
                set_levelName(e.target.value);
                setError(null);
                setSuccess(null);
              },
            }}
          />
          <Select
            props={{
              id: "isCity",
              title: "Is It a City?",
              required: true,
              option: ["no", "yes"],
              onChange: (e: any) => {
                if (e.target.selectedIndex === 0) setIsCity(0);
                else setIsCity(1);
                setError(null);
                setSuccess(null);
              },
            }}
          />
          <InputField
            props={{
              id: "population",
              title: "Population",
              type: "number",
              required: true,
              value: population ? population : "",
              onChange: (e: any) => {
                set_population(e.target.value);
                setError(null);
                setSuccess(null);
              },
            }}
          />
          <InputField
            props={{
              id: "gdp",
              title: "GDP",
              type: "number",
              required: true,
              value: gdp ? gdp : "",
              onChange: (e: any) => {
                set_gdp(e.target.value);
                setError(null);
                setSuccess(null);
              },
            }}
          />
        </div>
      </AddSection>

      <AddSection
        title="Description"
        hint="A descriptive overview used on listings and detail pages."
        icon={<FiAlignLeft className="h-4 w-4" />}
        actions={
          <MyButton
            onClick={() => setShowDescriptionModal(true)}
            className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-br from-brand to-primary px-3 py-2 text-xs font-semibold text-white shadow hover:opacity-95"
          >
            <FaHandSparkles className="h-4 w-4" />
            Generate
          </MyButton>
        }
      >
        <TextArea
          props={{
            id: "description",
            title: "Description",
            type: "text",
            value: description ? description : "",
            onChange: (e: any) => {
              set_description(e.target.value);
              setError(null);
              setSuccess(null);
            },
          }}
        />
      </AddSection>

      <AddSection
        title="Safety & Security"
        hint="How travellers should perceive this destination."
        icon={<FiShield className="h-4 w-4" />}
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <Select
            props={{
              id: "tourism",
              title: "Tourism",
              required: true,
              option: ["safe", "intermediate", "danger"],
              onChange: (e: any) => {
                set_tourism(e.target.value);
                setError(null);
                setSuccess(null);
              },
            }}
          />
          <Select
            props={{
              id: "safety_indicator",
              title: "Safety Indicator",
              required: true,
              option: ["safe", "intermediate", "danger"],
              onChange: (e: any) => {
                set_safety_indicator(e.target.value);
                setError(null);
                setSuccess(null);
              },
            }}
          />
        </div>
      </AddSection>
    </AddPageShell>
  );
};

export default Add;
